import React from "react";
import { useTranslation } from "react-i18next";
import { AccountBadges } from "../Badges/AccountBadges";
import PlayerAvatar from "../PlayerAvatar";
import { Account } from "../../models/Account";
import { PlayerBadges } from "../../services/BadgeService";
import { GameBoardEntry } from "../../services/MinigameService";
import { GameBoardConfig } from "./gameBoards";
import styles from "./GameLeaderboard.module.css";

interface GameBoardRowsProps {
    config: GameBoardConfig;
    entries: GameBoardEntry[];
    /** The viewer's own row; pinned under a divider when it isn't already in `entries`. */
    me: GameBoardEntry | null;
    badges: PlayerBadges | null;
    avatarSize?: number;
}

/** Ranked rows for one game board — shared by the per-game panel and the full leaderboard page. */
const GameBoardRows: React.FC<GameBoardRowsProps> = ({ config, entries, me, badges, avatarSize = 24 }) => {
    const { t } = useTranslation("pages");
    const pinMe = me && !entries.some((e) => e.steamId === me.steamId);

    const renderRow = (entry: GameBoardEntry) => {
        const isMe = entry.steamId === me?.steamId;
        // AccountBadges only reads the id and the two account flags the board carries.
        const account = { steamID: entry.steamId, playerName: entry.name, premium: entry.premium, donator: entry.donator } as Account;
        return (
            <div
                key={entry.steamId}
                className={`${styles.row} ${isMe ? styles.rowMe : ""}`}
                style={isMe ? { borderColor: config.accent } : undefined}
            >
                <span
                    className={`${styles.rank} ${entry.rank <= 3 ? styles.rankTop : ""}`}
                    style={entry.rank === 1 ? { color: config.accent } : undefined}
                >
                    {entry.rank}
                </span>
                <PlayerAvatar steamId={entry.steamId} name={entry.name} size={avatarSize} badges={badges} />
                <span className={styles.name}>
                    <span className={styles.nameText}>{entry.name}</span>
                    <span className={styles.badges}>
                        <AccountBadges account={account} badges={badges} />
                    </span>
                    {isMe && <span className={styles.youBadge}>{t("gamesLeaderboard.you")}</span>}
                </span>
                <span className={styles.score}>{config.formatScore(entry)}</span>
            </div>
        );
    };

    return (
        <div className={styles.rows}>
            {entries.map(renderRow)}
            {pinMe && (
                <>
                    <div className={styles.divider} />
                    {renderRow(me!)}
                </>
            )}
        </div>
    );
};

export default GameBoardRows;
